import type { UploadZoneProcessState } from "@anna-money/anna-web-ui";
import {
  Icon,
  IconButton,
  Loader,
  Text,
  TextButton,
  UploadZone,
  useUploadZoneController,
} from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { FC, MouseEvent, ReactElement, ReactNode } from "react";
import { useEffect, useState } from "react";
import { ErrorView } from "../../../../components/errorView/errorView";
import type { Form } from "../../../../components/form/form";
import { ImageIcon } from "../../../../icons/icons";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { ModalHeaderForm, ObPaymentPageFormStore } from "../../obPaymentPageFormStore";
import { CompanyLogoSection } from "../companyLogoSection/companyLogoSection";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import styles from "./HeaderSection.module.scss";

const imageTypes = { "image/jpeg": [], "image/png": [], "image/gif": [] };

// handleSelect writes the preview into either the page form or the modal form
type BackgroundImageFields = {
  onChange: (field: "backgroundImage", value: Nullable<string>) => void;
};

// readAsDataURL always produces a string result
const readerResultAsString = (reader: FileReader): string | null =>
  typeof reader.result === "string" ? reader.result : null;

type ImageUploaderProps = {
  className?: string;
  initialPreview?: string | null;
  form: Form<PaymentPageForm>;
  modalForm: Form<ModalHeaderForm>;
  store: ObPaymentPageFormStore;
};

export const ImageUploaderSection: FC<ImageUploaderProps> = observer(
  ({ initialPreview = null, store, form, modalForm }) => {
    const [preview, setPreview] = useState<string | null>(initialPreview);
    const [file, setFile] = useState<File>();
    const [state, onProcessStateChange] = useState<UploadZoneProcessState>("ready");

    const [editMode, setEditMode] = useState(false);
    const logoUploadController = useUploadZoneController();

    const handleSelect = (fileValue: File, formData: BackgroundImageFields): void => {
      form.onChange("backgroundImageFile", fileValue);
      form.onChange("backgroundImageFileData", null);

      if (fileValue) {
        const reader = new FileReader();
        reader.onloadend = (): void => {
          formData.onChange("backgroundImage", readerResultAsString(reader));
          store.clearValidationError("imageSize");
        };
        reader.readAsDataURL(fileValue);
      }
    };

    const onSelectHandler = (fileValue: File, formData: BackgroundImageFields): void => {
      setFile(fileValue);
      handleSelect(fileValue, formData);
    };

    // UploadZone expects a promise; the work itself is synchronous
    const onSelectLogoHandler = async (fileValue: File): Promise<void> => {
      form.onChange("logoFile", fileValue);
      form.onChange("logoFileData", null);
      if (fileValue) {
        const reader = new FileReader();
        reader.onloadend = (): void => {
          modalForm.onChange("logo", readerResultAsString(reader));
          store.clearValidationError("imageSize");
        };
        reader.readAsDataURL(fileValue);
      }
      await Promise.resolve();
    };

    useEffect(() => {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = (): void => {
          setPreview(readerResultAsString(reader));
        };
        reader.readAsDataURL(file);
      }
    }, [file]);

    const onRemoveHandler = (e?: MouseEvent<HTMLButtonElement>): void => {
      form.onChange("backgroundImageFile", null);
      form.onChange("backgroundImageFileData", null);
      e?.stopPropagation();
      setPreview(null);
      modalForm.onChange("backgroundImage", null);
      store.clearValidationError("imageSize");
    };

    const renderContent = (): ReactNode => {
      if (state === "uploading") {
        return <Loader />;
      }

      return (
        <div className={styles.content}>
          {preview ? (
            <>
              <div
                className={styles.preview}
                style={{
                  backgroundImage: `url(${preview})`,
                }}
              />
              {editMode ? (
                <div className={styles.buttonsBlock}>
                  <div className={styles.changeButton}>
                    <UploadZone
                      onSelect={async (fileValue) => {
                        onSelectHandler(fileValue, modalForm);
                        await Promise.resolve();
                      }}
                      className={styles.changeUploader}
                    >
                      <IconButton
                        title="Change background image"
                        className={styles.icon}
                        icon="actionEdit"
                        type="primary"
                        size="s"
                      />
                      <Text weight="semibold" type="body1" color="themeGreen">
                        Change
                      </Text>
                    </UploadZone>
                  </div>
                  <div className={styles.removeButton}>
                    <IconButton
                      title="Remove background image"
                      className={styles.icon}
                      icon="actionClose"
                      type="primary"
                      size="l"
                      onClick={(e) => {
                        onRemoveHandler(e);
                      }}
                    />
                    <Text weight="semibold" type="body1" color="themeGreen">
                      Remove
                    </Text>
                  </div>
                </div>
              ) : (
                <IconButton
                  title="Edit background image"
                  className={styles.editButton}
                  icon="actionEdit"
                  type="primary"
                  size="s"
                  onClick={(e) => {
                    e?.stopPropagation();
                    setEditMode(true);
                  }}
                />
              )}
            </>
          ) : (
            <>
              <ImageIcon width="37" height="37" />
              <div className={styles.label}>+ Add hero image</div>
            </>
          )}
        </div>
      );
    };

    const handleSave = (): void => {
      form.onMultipleChange([
        { field: "backgroundImage", value: modalForm.getValue("backgroundImage") },
        { field: "logo", value: modalForm.getValue("logo") },
        { field: "switchedOnLogo", value: modalForm.getValue("switchedOnLogo") },
      ]);
      setEditMode(false);
    };

    const handleCancel = (): void => {
      modalForm.onMultipleChange([
        { field: "backgroundImage", value: form.getValue("backgroundImage") },
        { field: "logo", value: form.getValue("logo") },
        { field: "switchedOnLogo", value: form.getValue("switchedOnLogo") },
      ]);
      setEditMode(false);
    };

    const renderUploadZone = (): ReactElement => (
      <UploadZone
        accept={imageTypes}
        multiple={false}
        className={classNames(styles.wrapper, styles["theme-default"], styles.uploader)}
        onStateChange={onProcessStateChange}
        onSelect={async (fileValue) => {
          onSelectHandler(fileValue, form);
          await Promise.resolve();
        }}
      >
        {renderContent()}
      </UploadZone>
    );

    const renderLogoSection = (): ReactElement => {
      const logo = modalForm.getValue("logo");
      return (
        <div className={classNames(styles.logoWrapper, editMode && preview && styles.withSeparator)}>
          {logo ? (
            <>
              <span className={styles.sectionSubtitle}>Company logo</span>
              <div className={styles.logo}>
                <img className={styles.headerLogo} src={logo} alt="" />
                <IconButton
                  title="Remove logo"
                  icon="actionCloseCircle"
                  className={styles.iconClose}
                  iconColor="themeGreen"
                  size="s"
                  onClick={() => {
                    form.onChange("logoFile", null);
                    form.onChange("logoFileData", null);
                    modalForm.onChange("logo", null);
                    store.clearValidationError("imageSize");
                  }}
                />
              </div>
              <UploadZone
                onSelect={onSelectLogoHandler}
                controller={logoUploadController}
                mode="drag-only"
                className={styles.uploadZoneButton}
              >
                <TextButton
                  size="l"
                  className={styles.addButton}
                  text="Choose another logo"
                  prefix={<Icon type="actionAdd" />}
                  onClick={logoUploadController.openPicker}
                />
              </UploadZone>
            </>
          ) : (
            <CompanyLogoSection store={store} form={form} modalForm={modalForm} withSeparator={false} view="modal" />
          )}
        </div>
      );
    };

    return (
      <>
        {editMode ? (
          <EditFormModalSection open={editMode} onSave={handleSave} onCancel={handleCancel} withMargin={false}>
            <div>
              {renderUploadZone()}
              {renderLogoSection()}
            </div>
          </EditFormModalSection>
        ) : (
          renderUploadZone()
        )}
      </>
    );
  },
);

type HeaderSectionProps = {
  modalForm: Form<ModalHeaderForm>;
  form: Form<PaymentPageForm>;
  store: ObPaymentPageFormStore;
};

export const HeaderSection: FC<HeaderSectionProps> = observer(({ form, store, modalForm }) => {
  const [showBanner, toggleShowBanner] = useState(store.firstUse);

  return (
    <>
      <div className={styles.headerSection}>
        <span className={styles.pageTitle}>Your payment page</span>
        {showBanner && (
          <div className={styles.banner}>
            <span className={styles.text}>
              Here&apos;s what appears on your page. Update the info you want to show, or hide the bits you don&apos;t
              need
            </span>
            <IconButton
              title="Dismiss"
              size="l"
              onClick={() => {
                toggleShowBanner(false);
              }}
              iconColor="themeGreen"
              icon="actionClose"
            />
          </div>
        )}
        <div className={styles.header}>
          <>
            <ImageUploaderSection
              form={form}
              modalForm={modalForm}
              store={store}
              initialPreview={form.getValue("backgroundImage") ?? null}
            />
          </>
          {!!form.getValue("logo") && (
            <div className={styles.logoBlock}>
              <>
                <img className={styles.headerLogo} src={form.getValue("logo") ?? undefined} alt="" />
                <IconButton
                  title="Remove logo"
                  icon="actionCloseCircle"
                  className={styles.iconClose}
                  iconColor="themeGreen"
                  size="s"
                  onClick={() => {
                    form.onChange("logoFile", null);
                    form.onChange("logoFileData", null);
                    form.onChange("logo", null);
                    modalForm.onChange("logo", null);
                    store.clearValidationError("imageSize");
                  }}
                />
              </>
            </div>
          )}
        </div>

        {!!store.getValidationError("imageSize") && (
          <div id="imageSize" className={classNames(styles.errorView, form.getValue("logo") && styles.withMargin)}>
            <ErrorView>{store.getValidationError("imageSize")}</ErrorView>
          </div>
        )}
      </div>
    </>
  );
});
